import uuid from 'uuid';

import { api } from 'redux/api/index.api';
import Analytics from 'utils/Analytics';
import { documentType, locationType } from 'utils/constants';

export const documentsApi = api.injectEndpoints({
  endpoints: builder => ({
    fetchDocument: builder.query({
      query: ({ projectId, documentId }) => ({
        url: `projects/${projectId}/documents/${documentId}`,
        method: 'GET',
        headers: {
          'Cache-Control': 'No-Store',
          'Content-Type': 'application/json',
        },
      }),
      providesTags: (result, error, { documentId }) => [{ type: 'Document', id: documentId }],
    }),
    fetchDocuments: builder.query({
      query: ({ projectId, parentId, searchValue, filters, limit, offset }) => ({
        url: `projects/${projectId}/documents/filter?limit=${limit}&offset=${offset}`,
        method: 'POST',
        body: {
          ...filters,
          assignee_ids: [
            ...(filters?.assignee_ids ? filters?.assignee_ids : []),
            ...(filters?.assigned_to_me ? filters?.assigned_to_me : []),
          ],
          parent_id: parentId,
          search_term: searchValue,
        },
      }),
      providesTags: ['DocumentsList'],
    }),
    fetchFoldersContent: builder.query({
      query: ({ projectId, id }) => {
        const folderQuery = id ? `document_id=${id}&` : '';
        return {
          url: `projects/${projectId}/documents/children-folders?${folderQuery}offset=0&limit=500`,
          method: 'GET',
          headers: { 'Cache-Control': 'No-Store' },
        };
      },
      transformResponse: (response, meta, arg) =>
        arg.id
          ? response.documents
          : response.documents
              .filter(element => element.parent_id === null)
              .sort((a, b) => a.name.localeCompare(b.name)),
      providesTags: ['DocumentsList'],
    }),
    fetchPlanFoldersContent: builder.query({
      query: ({ projectId, id }) => {
        const folderQuery = id ? `/${id}/children-folders?` : '?';
        return {
          url: `projects/${projectId}/folders${folderQuery}offset=0&limit=500`,
          method: 'GET',
          headers: { 'Cache-Control': 'No-Store' },
        };
      },
      transformResponse: (response, meta, arg) =>
        arg.id
          ? response.folders
          : response.folders
              .filter(element => !element.parent_folder_id || element.parent_folder_id === null)
              .sort((a, b) => a.name.localeCompare(b.name)),
      invalidatesTags: ['DocumentsList'],
    }),
    createAsPlan: builder.mutation({
      query: ({ projectId, folderId, document }) => {
        const payload = {
          plan_id: uuid.v4(),
          folder_id: folderId !== 'root' ? folderId : '',
          plan_type: locationType.plan,
          name: document.name,
          document_id: document.id,
          media_resource_from_document: {
            media_resource_id: document.media_resource.id,
            media_resource_mime_type: document.media_resource.mime_type,
            media_resource_display_name: document.media_resource.name,
            media_resource_md5: document.media_resource.md5,
            media_resource_size: document.media_resource.size,
            media_resource_width: document.media_resource.width,
            media_resource_height: document.media_resource.height,
            media_resource_data: document.media_resource.data,
            url: document.media_resource.url,
          },
          media_resource: {
            root_media_resource_id: document.media_resource.id,
            media_resource_id: document.media_resource.id,
            media_resource_label: document.name,
          },
        };
        return {
          url: `v1.3/projects/${projectId}/plans`,
          method: 'POST',
          headers: {
            'Cache-Control': 'No-Store',
            'Content-Type': 'application/json',
          },
          body: payload,
        };
      },
      invalidatesTags: ['DocumentsList'],
    }),
    deleteDocument: builder.mutation({
      query: ({ projectId, id }) => ({
        url: `projects/${projectId}/documents/${id}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['DocumentsList'],
    }),
    renameDocument: builder.mutation({
      query: ({ projectId, id, name, parentId }) => ({
        url: `projects/${projectId}/documents/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { name, parent_id: parentId },
      }),
      invalidatesTags: ['DocumentsList'],
    }),
    moveItemsTo: builder.mutation({
      query: ({ projectId, parentId, items }) => {
        const payload = {
          parent_id: parentId !== 'root' ? parentId : null,
          moved_document_ids: items.map(i => i.id),
        };

        return {
          url: `projects/${projectId}/documents/change-parent-folder`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: payload,
        };
      },
      transformResponse: response => {
        if (response[0].parent_id) {
          Analytics.track('documents_folder_moved');
        } else {
          Analytics.track('documents_file_moved');
        }
        return response;
      },
      invalidatesTags: ['DocumentsList'],
    }),
    createDocument: builder.mutation({
      query: ({ projectId, file, uploadedFile, folderId }) => ({
        url: `v1.2/projects/${projectId}/documents`,
        method: 'POST',
        body: {
          id: uuid.v4(),
          type: documentType.document,
          name: file.name,
          parent_id: folderId,
          media_resource: {
            root_media_resource_id: uploadedFile.id,
            media_resource_id: uploadedFile.id,
            media_resource_label: uploadedFile.file_name,
          },
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('documents_file_created', {
              file_format: data.media_resource.media_resource_mime_type,
              size: data.media_resource.media_resource_size,
              source: 'documents',
            });
          }
        } catch (error) {
          console.error('Error creating document:', error);
        }
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useFetchDocumentQuery,
  useFetchDocumentsQuery,
  useLazyFetchDocumentsQuery,
  useFetchFoldersContentQuery,
  useLazyFetchFoldersContentQuery,
  useLazyFetchPlanFoldersContentQuery,
  useCreateAsPlanMutation,
  useDeleteDocumentMutation,
  useRenameDocumentMutation,
  useMoveItemsToMutation,
  useCreateDocumentMutation,
} = api;
